import { CodeableConceptDisplay } from '@medplum/react'
import { CARE_TEAM_CATEGORIES } from '@seen/core/models/care-teams/constants'
import { IDT } from '@seen/core/models/care-teams/idt'
import type { Participant } from '@seen/core/models/patients/participant'
import { Button, TypographySubtle } from '@seen/ui'
import { IconHistory, IconPencil, IconUsersGroup } from '@tabler/icons-react'
import { isEmpty } from 'lodash-es'
import { type JSX, useState } from 'react'
import { CareTeamEditDialog } from '#src/components/care-team/CareTeamEditDialog.tsx'
import { CareTeamHistory } from '#src/components/care-team/CareTeamHistory.tsx'
import { Dialog } from '#src/components/core/dialog/Dialog.tsx'
import { DialogContent } from '#src/components/core/dialog/DialogContent.tsx'
import { DialogHeader } from '#src/components/core/dialog/DialogHeader.tsx'
import { DialogTitle } from '#src/components/core/dialog/DialogTitle.tsx'
import { DialogDisplay } from '#src/components/core/dialog/display/DialogDisplay.tsx'
import { ResourceCard } from '#src/components/core/ResourceCard.tsx'
import { DevMedplumAppLinkOverlay } from '#src/components/medplum/DevMedplumAppLinkOverlay.tsx'
import {
  FacesheetCard,
  FacesheetCardActions,
  FacesheetCardCollapseTrigger,
  FacesheetCardContent,
  FacesheetCardHeader,
  FacesheetCardIcon,
  FacesheetCardLabel,
  FacesheetCardTitle,
} from '#src/components/patient/detail-content/facesheet/index.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useSearchOne } from '#src/hooks/useSearchOne.ts'

export function CareTeamCard({
  participant,
}: {
  participant: Participant
}): JSX.Element {
  const isReadOnly = useIsReadOnly()
  const [editOpen, setEditOpen] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)
  const { data: idt } = useSearchOne(IDT, {
    subject: participant.reference,
    category: CARE_TEAM_CATEGORIES.IDT,
  })

  return (
    <>
      <FacesheetCard rumName="care_team">
        <FacesheetCardHeader>
          <FacesheetCardTitle className="relative">
            <FacesheetCardIcon>
              <IconUsersGroup />
            </FacesheetCardIcon>
            <FacesheetCardLabel>Care Team</FacesheetCardLabel>
            <FacesheetCardCollapseTrigger />
            {idt && <DevMedplumAppLinkOverlay to={idt} />}
          </FacesheetCardTitle>
          <FacesheetCardActions className="z-10 gap-1">
            {idt && (
              <Button
                variant="ghost"
                size="icon"
                className="size-6"
                aria-label="Care team history"
                onClick={() => setHistoryOpen(true)}
              >
                <IconHistory className="size-3.5" />
              </Button>
            )}
            {!isReadOnly && (
              <Button
                variant="ghost"
                size="icon"
                className="size-6"
                aria-label="Edit care team"
                onClick={() => setEditOpen(true)}
              >
                <IconPencil className="size-3.5" />
              </Button>
            )}
          </FacesheetCardActions>
        </FacesheetCardHeader>
        <FacesheetCardContent>
          {isEmpty(idt?.members) && (
            <TypographySubtle className="text-foreground-secondary">
              No care team
            </TypographySubtle>
          )}
          {idt?.members.map((member) => (
            <ResourceCard
              key={member.practitioner.reference}
              value={member.practitioner}
              detail={
                <TypographySubtle className="text-foreground-secondary">
                  <CodeableConceptDisplay value={member.role[0]} />
                </TypographySubtle>
              }
            />
          ))}
        </FacesheetCardContent>
      </FacesheetCard>
      <CareTeamEditDialog
        participant={participant}
        idt={idt ?? undefined}
        open={editOpen}
        onOpenChange={setEditOpen}
      />
      {idt && (
        <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
          <DialogDisplay>
            <DialogHeader className="py-4 text-lg">
              <DialogTitle>Care Team History</DialogTitle>
            </DialogHeader>
            <DialogContent className="pt-0 pb-4">
              <CareTeamHistory idt={idt} />
            </DialogContent>
          </DialogDisplay>
        </Dialog>
      )}
    </>
  )
}
